
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SidebarProps } from './types';
import MenuCategoryItem from './MenuCategoryItem';
import { InviteUserButton } from './InviteUserButton';
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';

// v2 ProyectMind Logo SVG (white version for app sidebar)
const PMLogoIcon = () => (
  <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#7C3AED"/>
    <rect x="7" y="7" width="8" height="8" rx="2.5" fill="white" fillOpacity="0.9"/>
    <rect x="17" y="7" width="8" height="8" rx="2.5" fill="#F59E0B"/>
    <rect x="7" y="17" width="8" height="8" rx="2.5" fill="#EC4899"/>
    <rect x="17" y="17" width="8" height="8" rx="2.5" fill="white" fillOpacity="0.35"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <circle cx="6.5" cy="6.5" r="4.5" stroke="#94A3B8" strokeWidth="1.5"/>
    <path d="M10 10L13.5 13.5" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const Sidebar = ({ compactSidebar, menuCategories, onNavigate, isActive, onResumeOnboarding }: SidebarProps) => {
  const navigate = useNavigate();
  const lastCategoryIndex = menuCategories.length - 1;

  return (
    <aside
      className={`hidden md:flex flex-col ${compactSidebar ? 'w-16' : 'w-64'} transition-all duration-300`}
      style={{
        background: 'white',
        borderRight: '1px solid #E2E8F0',
        height: '100%',
      }}
    >
      {/* Workspace selector */}
      {!compactSidebar && (
        <div className="px-3 py-3" style={{ borderBottom: '1px solid #E2E8F0' }}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/dashboard')}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard'); }}
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-all hover:shadow-sm"
            style={{ background: '#F8F9FB', border: '1px solid #E2E8F0' }}
          >
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-black flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #7C3AED, #EC4899)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              PM
            </div>
            <div className="flex-1 min-w-0">
              <div style={{ color: '#0F0E17', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.01em' }}>
                Mi Workspace
              </div>
              <div style={{ color: '#94A3B8', fontSize: '0.62rem', fontFamily: 'Manrope, sans-serif' }}>
                Plan PRO
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Navigation */}
      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
        {menuCategories.map((category, index) => {
          const categoryElement = (
            <MenuCategoryItem
              key={index}
              category={category}
              index={index}
              compactSidebar={compactSidebar}
              isOpen={false}
              onToggle={() => {}}
              onNavigate={onNavigate}
              isActive={isActive}
            />
          );

          if (index === lastCategoryIndex) {
            return (
              <div key={index}>
                {categoryElement}
                <div className="px-2 py-3 space-y-3">
                  <InviteUserButton compactSidebar={compactSidebar} />
                </div>
              </div>
            );
          }

          return categoryElement;
        })}
      </nav>

      {/* Onboarding checklist (solo si está pendiente) */}
      {!compactSidebar && (
        <OnboardingChecklist
          compactSidebar={compactSidebar}
          onResumeWizard={() => onResumeOnboarding?.()}
        />
      )}

      {/* Upgrade card */}
      {!compactSidebar && (
        <div className="p-3" style={{ borderTop: '1px solid #E2E8F0' }}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/settings?tab=billing')}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/settings?tab=billing'); }}
            className="rounded-xl p-3 cursor-pointer transition-transform hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg, #7C3AED, #EC4899)' }}
          >
            <div style={{ color: 'white', fontSize: '0.75rem', fontWeight: 700, fontFamily: 'Manrope, sans-serif', marginBottom: '4px' }}>
              Desbloquea todo el potencial
            </div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem', fontFamily: 'Manrope, sans-serif', marginBottom: '8px' }}>
              IA Predictiva, proyectos ilimitados y más.
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); navigate('/settings?tab=billing'); }}
              style={{
                background: 'white',
                color: '#7C3AED',
                fontSize: '0.72rem',
                fontWeight: 700,
                fontFamily: 'Manrope, sans-serif',
                border: 'none',
                borderRadius: '8px',
                padding: '6px 12px',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Subir a PRO →
            </button>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
